// src/components/SendFaxModal.jsx
// The Compose form (3.27.0), on the Compose tab (inline) and as the Send Fax
// pop-up in Outbox. Receivers are chips picked from Contacts or typed (one
// fax each; one receiver at a time for HIPAA clients, up to 10 otherwise);
// several attachments, each its own fax with an optional cover sheet (3.64.0,
// owner: like fax by email; photos joined into one; built on the server, routes/portal.js
// /send-fax); Preview shows the faxes as they will go out; Send Confirmation
// (3.64.0, the client's setting) asks to check them first (SendCheck); Schedule sends it later. "Send from" shows at the top
// when there's more than one line or team to pick (3.27.0: was under
// Advanced). The receiver name of a typed number new to the client is saved
// to Contacts on sending (not for contacts picked from the list).
// 3.66.0 (owner, Oct 23): when someone replaces a number's name (e.g.
// "Toll-free number" with the company's real name), they're asked whether
// to save it for that number for every client (company names only; clients
// with their own name for it keep theirs). 3.66.1 (owner): asked before
// sending; saved once the fax has gone.
import { fax as faxOk, display as showPhone } from '../utils/phone';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from './Notifications';
import { API_BASE } from '../firebase/config';
import Emo, { emoLabel } from './Emo';
import { companyName, tidyName } from '../utils/receiverName';
import { RichText } from './EmailEditorParts';
import { MAX_CHARS, fromPlain, toText, countChars, cleanPaste, coverPagesAbout } from '../utils/coverMessage';
import { watchFaxesFast } from '../hooks/useFaxNotices';
import { pathForTab } from '../utils/tabRoutes';
import { usePhone } from '../hooks/useWindowWidth';
import PdfPages, { needsPdfPages } from './PdfPages';
import SendCheck, { faxCount, faxGroups } from './SendCheck';
import { useProblemNumbers } from '../hooks/useProblemNumbers';
import SendTracker from './SendTracker';
import { requestOpenFax } from '../utils/openFaxRequest';

// 3.62.3 (owner, 2026-10-15): up to 50 MB in all. Above DIRECT the files go
// straight into the backend's temp storage (a request can't be over 32 MB)
const MAX_TOTAL = 50 * 1024 * 1024;
const DIRECT = 15 * 1024 * 1024;
const SPLIT_OVER = 150, PART_MAX = 100;   // 3.62.20: over 150 pages goes as parts of at most 100 (backend lib/composeFax.js)
const MAX_RECEIVERS = 10;
const WORD = /\.(docx?)$/i, IMAGE = /\.(jpe?g|png)$/i, PDF = /\.pdf$/i;
const fmtLine = n => { const d = String(n || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n; };
const fmtNumber = n => { const d = String(n || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n; };
const digitsOf = n => String(n || '').replace(/\D/g, '');
// 3.49.17: a real US or Canada number (utils/phone.js), not just 10 digits
const validNumber = n => faxOk(n).ok;
const sizeText = b => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const readBase64 = f => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(',')[1]); r.onerror = reject; r.readAsDataURL(f); });
// Pages of a PDF, counted in the browser (a close guess; the server counts exactly)
async function pdfPages(file) {
  try {
    const text = await file.text();
    const m = text.match(/\/Type\s*\/Page(?!s)/g);
    return m ? m.length : null;
  } catch { return null; }
}
// A date-time box's value (local time) for a moment
const localInput = d => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

export default function SendFaxModal({ onClose, onSuccess, inline = false }) {
  const { getToken, effectiveInfo } = useAuth();
  const { confirm, notify } = useNotify();
  const phone = usePhone();   // 3.51.0: no dragging on phones; the picker also offers the camera
  // Super Admins on "All clients" (3.27.0): Send as client — Crazy Pixels by
  // default; the picked client's rules (HIPAA), lines and contacts apply
  const asAdmin = effectiveInfo?.accountId === 'all' && effectiveInfo?.roleId === 'super_admin';
  const [clients, setClients] = useState(null);   // [{ id, name, hipaa, from: [{ teamId, number, label }] }]
  const [asClient, setAsClient] = useState('crazypixels');
  const [fromPick, setFromPick] = useState(0);
  useEffect(() => {
    if (!asAdmin) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/compose/clients`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        setClients(j.data || []); if (j.defaultId) setAsClient(j.defaultId);
      } catch { setClients([]); }
    })();
  }, [asAdmin, getToken]);
  const client = asAdmin ? (clients || []).find(c => c.id === asClient) : null;
  const hipaa = asAdmin ? !!client?.hipaa : !!effectiveInfo?.hipaa;
  const maxReceivers = hipaa ? 1 : MAX_RECEIVERS;
  // 3.64.0 (owner): the client asks everyone to check before a fax goes
  const sendConfirm = asAdmin ? !!client?.sendConfirm : !!effectiveInfo?.sendConfirm;
  // Send from: the line it goes out on — teams (3.19.0) or an individual
  // client's lines; for Super Admins, the picked client's lines and teams
  const teams = asAdmin ? [] : effectiveInfo?.teams || [];
  const [teamId, setTeamId] = useState(effectiveInfo?.defaultTeam || teams[0]?.id || '');
  const lines = asAdmin ? [] : effectiveInfo?.lines || [];
  // My Default Line (3.30.0, Settings) if it's one of their lines
  const [line, setLine] = useState(() => (lines.some(l => l.number === effectiveInfo?.defaultLine) ? effectiveInfo.defaultLine : lines[0]?.number || ''));
  const adminFrom = client?.from?.[fromPick] || client?.from?.[0] || null;
  const fromText = asAdmin ? (adminFrom ? `${adminFrom.label ? `${adminFrom.label} · ` : ''}${fmtLine(adminFrom.number)}` : '')
    : teams.length ? (() => { const t = teams.find(x => x.id === teamId) || teams[0]; return `${t.name} · ${fmtLine(t.number)}`; })()
    : lines.length ? fmtLine(line) : '';

  // Receivers (chips) and the type-ahead from Contacts (3.26.0)
  const [receivers, setReceivers] = useState([]);   // [{ number, name }]
  const problemOf = useProblemNumbers();   // 3.60.0: problem numbers (Logs → Problem numbers)
  const [typed, setTyped] = useState('');
  const [suggest, setSuggest] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  useEffect(() => {
    const q = typed.trim();
    if (!q) { setSuggest([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/contacts/suggest?q=${encodeURIComponent(q)}${asAdmin ? `&account=${encodeURIComponent(asClient)}` : ''}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        setSuggest(j.data || []); setHi(-1); setSuggestOpen(true);
      } catch { setSuggest([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [typed, getToken, asAdmin, asClient]);
  // fromContact: picked from Contacts (its name is never saved back)
  function addReceiver(number, name = '', fromContact = false) {
    setError('');
    if (!validNumber(number)) { setError(`${number} isn't a valid US or Canada fax number. Faxes go to US and Canada numbers only.`); return false; }
    const d = digitsOf(number).slice(-10);
    if (receivers.some(r => digitsOf(r.number).slice(-10) === d)) { setTyped(''); return true; }
    if (receivers.length >= maxReceivers) {
      setError(hipaa ? 'Your company handles health information, so a fax goes to one receiver at a time. Send it again for the next receiver.' : `Up to ${MAX_RECEIVERS} receivers at a time.`);
      return false;
    }
    // shown: the name it came with, to tell when someone replaces it (3.66.0)
    setReceivers(rs => [...rs, { number: '1' + d, name: name || '', shown: name || '', ...(fromContact ? { fromContact: true } : {}) }]);
    setTyped(''); setSuggest([]); setSuggestOpen(false);
    // A typed number that's already in Contacts gets its name (3.32.1)
    if (!fromContact) findContact(d).then(c => c && setReceivers(rs => rs.map(r => (digitsOf(r.number).slice(-10) === d && !r.name ? { ...r, name: c.name || '', shown: c.name || '', fromContact: true } : r))));
    return true;
  }
  // The client's contact with exactly this number, or null
  async function findContact(d) {
    const hit = suggest.find(c => digitsOf(c.number).slice(-10) === d);
    if (hit) return hit;
    try {
      const r = await fetch(`${API_BASE}/portal/contacts/suggest?q=${d}${asAdmin ? `&account=${encodeURIComponent(asClient)}` : ''}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      return ((await r.json()).data || []).find(c => digitsOf(c.number).slice(-10) === d) || null;
    } catch { return null; }
  }
  // A whole number typed in the receiver box (not added yet)
  const typedNumber = typed.trim() && validNumber(typed) ? typed.trim() : '';
  const removeReceiver = n => setReceivers(rs => rs.filter(r => r.number !== n));

  // Contacts picker (toolbar)
  const [picker, setPicker] = useState(null);   // null | { rows, q, picked:Set }
  async function openPicker() {
    setPanel(null);
    setPicker({ rows: null, q: '', picked: new Set() });
    try {
      const r = await fetch(`${API_BASE}/portal/contacts${asAdmin ? `?account=${encodeURIComponent(asClient)}` : ''}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      setPicker(p => p && { ...p, rows: (j.data || []).filter(c => !c.removed && (!asAdmin || c.accountId === asClient)) });
    } catch { setPicker(p => p && { ...p, rows: [] }); }
  }

  // Attachments
  const [files, setFiles] = useState([]);   // [{ file, pages }]
  const fileRef = useRef();
  const [drag, setDrag] = useState(false);
  async function addFiles(list) {
    setError('');
    const next = [...files];
    for (const f of list) {
      if (!WORD.test(f.name) && !IMAGE.test(f.name) && !PDF.test(f.name)) { setError(`${f.name}: only PDF, JPG, PNG, DOC or DOCX files can be attached.`); continue; }
      if (next.some(x => x.file.name === f.name && x.file.size === f.size)) continue;
      next.push({ file: f, pages: PDF.test(f.name) ? await pdfPages(f) : IMAGE.test(f.name) ? 1 : null });
    }
    if (next.reduce((n, x) => n + x.file.size, 0) > MAX_TOTAL) { setError('The attachments add up to more than 50 MB. Split the document into 2 or 3 smaller faxes.'); return; }
    setFiles(next);
    if (fileRef.current) fileRef.current.value = '';
  }
  // 3.57.2: Word files are turned into PDFs by the backend (Google Drive), so
  // they join the others, take a cover sheet and show in Preview; their page
  // count is only known after that (Preview shows it)
  const hasWord = files.some(x => WORD.test(x.file.name));
  const attachedPages = files.every(x => x.pages != null) ? files.reduce((n, x) => n + x.pages, 0) : null;

  // Cover sheet: on by default; the confidentiality notice is on by default
  // for HIPAA clients and can be ticked for others
  // The client's Cover Sheet defaults (3.30.0, Settings → Cover Sheet), else
  // on, notice for HIPAA clients, no message
  const coverDefaults = asAdmin ? client?.coverDefaults : effectiveInfo?.coverDefaults;
  const cdKey = JSON.stringify(coverDefaults || null) + hipaa;
  const [coverOn, setCoverOn] = useState(coverDefaults ? coverDefaults.on !== false : true);
  const [notice, setNotice] = useState(coverDefaults ? !!coverDefaults.notice : hipaa);
  const [subject, setSubject] = useState('');
  const [marks, setMarks] = useState(['urgent']);   // cover sheet ticks (3.32.0); Urgent unless another is picked (3.49.9)
  // 3.67.0 (owner): the message is formatted text (editor HTML), up to
  // 3,000 characters as before; msgKey reloads the editor when the message is reset
  // (3.67.1: the client's default keeps its formatting; the backend saves it
  // as safe HTML, lib/richText.js toHtml)
  const defaultMessage = d => d?.messageHtml || fromPlain(d?.message || '');
  const [message, setMessageRaw] = useState(defaultMessage(coverDefaults));
  const [msgKey, setMsgKey] = useState(0);
  const setMessage = html => { setMessageRaw(html); setMsgKey(k => k + 1); };
  const msgChars = useMemo(() => countChars(message), [message]);
  useEffect(() => {
    setCoverOn(coverDefaults ? coverDefaults.on !== false : true);
    setNotice(coverDefaults ? !!coverDefaults.notice : hipaa);
    setMessage(defaultMessage(coverDefaults));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cdKey]);
  // Contact (3.32.0): Phone and Company print in the boxes at the top of the
  // cover sheet (name and email are there already); "Other contact info"
  // is anything else. Phone (3.49.9): the client's office number for business
  // clients, My Profile phone for individuals; Company and other info from
  // the client's Cover Sheet settings.
  // 3.62.25 (owner): ext = the sender's extension from My profile (editable here)
  const defaultContact = useMemo(() => ({ phone: (asAdmin ? client?.coverPhone : effectiveInfo?.coverPhone) || showPhone(effectiveInfo?.phone) || '', ext: asAdmin ? '' : (effectiveInfo?.phoneExt || ''), company: coverDefaults?.company || '', other: coverDefaults?.contact || '' }),
    [effectiveInfo, coverDefaults, asAdmin, client]);
  const [contact, setContact] = useState(defaultContact);
  // The contact fields follow the picked client until edited
  const contactEdited = useRef(false);
  useEffect(() => { if (!contactEdited.current) setContact(defaultContact); }, [defaultContact]);
  const setContactField = (k, v) => { contactEdited.current = true; setContact(c => ({ ...c, [k]: v })); };
  const cover = coverOn;
  // 3.67.0: a long message adds pages to each cover sheet (about; Preview shows the exact count)
  const coverEach = useMemo(() => (cover ? coverPagesAbout(message) : 0), [cover, message]);
  const totalPages = attachedPages == null ? null : attachedPages + faxGroups(files).length * coverEach;   // 3.64.0: a cover sheet per document (photos share one)

  // Toolbar panel: 'schedule' | null
  const [panel, setPanel] = useState(null);
  const [scheduleAt, setScheduleAt] = useState('');
  // 3.61.0 (owner): a scheduled time outside the client's office hours gets a warning (it can still be scheduled)
  const [hoursWarn, setHoursWarn] = useState('');
  useEffect(() => {
    if (!scheduleAt) { setHoursWarn(''); return undefined; }
    let gone = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/office-hours/check?at=${encodeURIComponent(new Date(scheduleAt).toISOString())}${asAdmin ? `&account=${encodeURIComponent(asClient)}` : ''}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        if (!gone) setHoursWarn(r.ok && !j.open ? j.label : '');
      } catch { if (!gone) setHoursWarn(''); }
    }, 300);
    return () => { gone = true; clearTimeout(t); };
  }, [scheduleAt, asAdmin, asClient, getToken]);
  const hoursNote = hoursWarn ? <div style={S.hoursWarn}><Emo e="⚠️" /> That time is outside your company’s office hours ({hoursWarn}). The office you’re faxing may be closed then. You can still schedule it.</div> : null;
  const [preview, setPreview] = useState(null);   // { url, pages } | { loading } | { error }

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [splitNote, setSplitNote] = useState('');   // 3.62.20: over 150 pages goes as parts
  const [result, setResult] = useState(null);
  // 3.70.0 (owner): after Send, the fax going out step by step ('track'),
  // or the Sent panel after Skip ('sent'; Watch it comes back)
  const [view, setView] = useState('track');
  const [progress, setProgress] = useState({});
  const [checking, setChecking] = useState(null);   // 3.64.0: SendCheck open → { list, resolve }

  // A large attachment is put in temp storage once, then Preview and Send
  // both name it (kept on the file's entry)
  async function uploadOnce(x) {
    if (x.tempPath) return x.tempPath;
    const type = x.file.type || (PDF.test(x.file.name) ? 'application/pdf' : '');
    const r = await fetch(`${API_BASE}/portal/compose/upload-url`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ name: x.file.name, type, size: x.file.size }) });
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || "Couldn't get the attachment ready.");
    const up = await fetch(j.url, { method: 'PUT', headers: { 'Content-Type': type }, body: x.file });
    if (!up.ok) throw new Error(`${x.file.name} couldn't be uploaded. Please try again.`);
    x.tempPath = j.path;
    return j.path;
  }
  async function body(list = receivers, confirmed = false, previewNumber = '') {
    const direct = files.reduce((n, x) => n + x.file.size, 0) > DIRECT;
    const encoded = await Promise.all(files.map(async x => (direct
      ? { name: x.file.name, mimeType: x.file.type, tempPath: await uploadOnce(x) }
      : { name: x.file.name, mimeType: x.file.type, base64: await readBase64(x.file) })));
    return {
      receivers: list, files: encoded,
      cover: cover ? { on: true, notice, subject, message: toText(message), messageHtml: message, marks, phone: contact.phone, ext: contact.ext || '', company: contact.company, contact: contact.other } : null,
      ...(scheduleAt ? { scheduleAt: new Date(scheduleAt).toISOString() } : {}),
      ...(confirmed ? { confirmed: true } : {}),
      ...(previewNumber ? { previewNumber } : {}),   // 3.64.1: whose faxes Preview shows
      ...(asAdmin ? { asClient, ...(adminFrom ? { fromNumber: adminFrom.number, ...(adminFrom.teamId ? { teamId: adminFrom.teamId } : {}) } : {}) }
        : { ...(teamId ? { teamId } : {}), ...(lines.length > 1 && line ? { fromNumber: line } : {}) }),
    };
  }
  // → the receivers to send to (a number typed but not added yet counts), or
  // null when something is missing
  function check({ forPreview = false } = {}) {
    let list = receivers;
    if (typed.trim() && validNumber(typed) && !forPreview) {
      const d = digitsOf(typed).slice(-10);
      if (!receivers.some(r => digitsOf(r.number).slice(-10) === d)) {
        if (receivers.length >= maxReceivers) { setError(hipaa ? 'Your company handles health information, so a fax goes to one receiver at a time.' : `Up to ${MAX_RECEIVERS} receivers at a time.`); return null; }
        list = [...receivers, { number: '1' + d, name: '' }];
        setReceivers(list); setTyped('');
      }
    } else if (typed.trim() && !forPreview) { setError(`${typed.trim()} isn't a fax number or a contact. Pick a contact from the list, or type a US or Canada fax number (10 digits).`); return null; }
    if (!forPreview && !list.length) { setError('Add a receiver: type a fax number or a contact\'s name.'); return null; }
    if (!files.length) { setError('Attach a document.'); return null; }
    if (cover && msgChars > MAX_CHARS) { setError(`The cover sheet message has ${msgChars.toLocaleString()} characters. Please shorten it to ${MAX_CHARS.toLocaleString()} or fewer.`); return null; }
    if (scheduleAt && Date.parse(scheduleAt) < Date.now() - 60000) { setError('The send time has passed. Pick a later time, or clear it to send now.'); return null; }
    return list;
  }
  // 3.64.1 (owner): `at` = which receiver's faxes to show (a picker in the
  // Preview's header when there are several); the first to start with
  async function showPreview(at) {
    setError('');
    const list = check({ forPreview: true });
    if (!list) return;
    const i = typeof at === 'number' && list[at] ? at : 0;
    setPreview({ loading: true, for: i });
    try {
      const r = await fetch(`${API_BASE}/portal/compose/preview`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(await body(list, false, list[i]?.number || '')) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't build the preview.");
      // 3.62.3: a large preview comes as a short-lived link instead
      const blob = j.url ? await (await fetch(j.url)).blob()
        : new Blob([Uint8Array.from(atob(j.base64), c => c.charCodeAt(0))], { type: 'application/pdf' });
      // 3.62.20 (owner): over 150 pages (the document's own) it goes as equal parts of at most 100
      // 3.64.0: each document is its own fax, so each is counted on its own
      const long = (j.faxes || []).filter(x => x.pages > SPLIT_OVER);
      setSplitNote(long.map(x => {
        const n = Math.ceil(x.pages / PART_MAX);
        return `${(j.faxes || []).length > 1 ? `${x.name} has` : 'This fax has'} ${x.pages} pages. It will go as ${n} parts of about ${Math.round(x.pages / n)} pages each, one after another${cover ? ', each with its own cover sheet' : ''}.`;
      }).join(' '));
      setPreview({ url: URL.createObjectURL(blob), pages: j.pages, blob, for: i });   // 3.56.3: the file itself, for PdfPages
    } catch (e) { setPreview(null); setError(e.message); }
  }
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  function clearAll() {
    setReceivers([]); setTyped(''); setFiles([]); setSubject(''); setMarks(['urgent']); setMessage(''); setScheduleAt(''); setPanel(null);
    setError(''); setSplitNote(''); setResult(null); contactEdited.current = false; setContact(defaultContact);
    setCoverOn(coverDefaults ? coverDefaults.on !== false : true); setNotice(coverDefaults ? !!coverDefaults.notice : hipaa); setMessage(defaultMessage(coverDefaults));
  }
  // Switching the client (Super Admins): its rules from then on
  function pickClient(id) {
    setAsClient(id); setFromPick(0); setError('');
    if (picker) setPicker(null);   // the picker showed the old client's contacts
    const next = (clients || []).find(c => c.id === id);
    if (next?.hipaa && receivers.length > 1) {
      setReceivers(rs => rs.slice(0, 1));
      setError(`${next.name} handles health information, so a fax goes to one receiver at a time. Only the first receiver was kept.`);
    }
    // its cover sheet defaults follow (the effect on cdKey)
  }

  async function handleSend() {
    setError(''); setResult(null);
    const list = check();
    if (!list) return;
    // 3.66.1 (owner): the name question comes before sending; Go back
    // returns to Compose with everything as it was
    const saveNames = await askSharedNames(list);
    if (!saveNames) return;
    // 3.64.0 (owner): the client's Send Confirmation: every receiver and
    // document (page 1 pictured) checked, then yes; it's noted in Activity
    if (sendConfirm) {
      const ok = await new Promise(resolve => setChecking({ list, resolve }));
      setChecking(null);
      if (!ok) return;
    } else if (list.length > 1) {
      const ok = await confirm({
        title: `Send to ${list.length} receivers?`,
        message: `Each of these gets ${faxGroups(files).length > 1 ? 'each document as its own fax' : 'its own fax'}${cover ? ' with its own cover sheet' : ''}:\n\n${list.map(r => `• ${r.name ? `${r.name} · ` : ''}${fmtNumber(r.number)}`).join('\n')}`,
        confirmLabel: scheduleAt ? 'Schedule' : 'Send',
      });
      if (!ok) return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API_BASE}/portal/send-fax`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(await body(list, sendConfirm)) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'The fax couldn\'t be sent.');
      setProgress({});
      setView('track');
      setResult(j);
      onSuccess?.(j.faxId);
      watchFaxesFast();   // 3.66.1: pop-ups and Overview follow it closely
      saveSharedNames(saveNames, j);
      // 3.67.1 (owner: the note under the form was easy to miss and went
      // after 8 s): the Sent panel takes the form's place, at the top, until
      // the person moves on
      if (inline) window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setError(e.message); }
    setSending(false);
  }

  // 3.66.0: names the sender typed in place of the one shown (company names
  // only) → asked once, before sending (3.66.1); → the ones to save, or
  // null for Go back (owner: a way back at this last step)
  async function askSharedNames(list) {
    const changed = list.filter(r => companyName(r.name) && tidyName(r.name).toLowerCase() !== tidyName(r.shown).toLowerCase());
    if (!changed.length) return [];
    const many = changed.length > 1;
    // Owner (Oct 23): just a confirmation, no explanation
    const more = sendConfirm || list.length > 1;   // another check follows
    const pick = await confirm({
      title: many ? 'Save these names for their fax numbers?' : `Save “${tidyName(changed[0].name)}” as the name for ${fmtLine(changed[0].number)}?`,
      ...(many ? { message: changed.map(r => `• ${tidyName(r.name)} for ${fmtLine(r.number)}`).join('\n') } : {}),
      choices: [{ value: 'save', label: many ? 'Save names' : 'Save name' }, { value: 'once', label: 'Just this fax' }],
      confirmLabel: more ? 'Next' : scheduleAt ? 'Schedule' : 'Send', cancelLabel: 'Go back',
    });
    return !pick ? null : pick === 'save' ? changed : [];
  }
  // Saved once their faxes went out
  async function saveSharedNames(changed, j) {
    const sent = new Set((j.results || []).filter(x => !x.error).map(x => digitsOf(x.number).slice(-10)));
    const names = changed.filter(r => sent.has(digitsOf(r.number).slice(-10)));
    if (!names.length) return;
    try {
      const r = await fetch(`${API_BASE}/portal/contacts/shared-name`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ names: names.map(x => ({ number: x.number, name: tidyName(x.name) })), ...(asAdmin ? { accountId: asClient } : {}) }) });
      const k = await r.json();
      if (!r.ok) throw new Error(k.error || "Couldn't save the names.");
      notify({ tone: 'success', title: (k.saved || []).length > 1 ? 'Names saved' : 'Name saved' });
    } catch (e) { notify({ tone: 'error', title: "Couldn't save the name", detail: e.message }); }
  }

  const receiverOf = x => { const rv = receivers.find(y => digitsOf(y.number).slice(-10) === digitsOf(x.number).slice(-10)); return `${rv?.name ? `${rv.name} · ` : ''}${fmtNumber(x.number)}`; };
  const resultText = r => {
    const name = x => `${receiverOf(x)}${files.length > 1 && x.fileName ? ` · ${x.fileName}` : ''}${x.part ? ` (part ${x.part.i} of ${x.part.n})` : ''}`;
    return (r.results || []).map(x => (x.error ? `✕ ${name(x)}: not sent (${x.error})`
      : x.scheduled ? `🕒 ${name(x)}: scheduled for ${new Date(x.scheduled).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`
      : x.waiting ? `⏳ ${name(x)}: waiting for the line (another fax to this number is going out)`
      : `✓ ${name(x)}: sent`));
  };

  // 3.70.0: the faxes that went (or wait for the line), for the tracker
  const trackItems = (result?.results || []).filter(x => x.ref && !x.error && !x.scheduled)
    .map(x => ({ ref: x.ref, faxId: x.faxId || null, waiting: !!x.waiting, label: receiverOf(x), fileName: x.fileName, part: x.part || null, pages: x.pages || null }));
  // Outbox (and a fax's panel there, 3.70.0)
  const openOutbox = faxId => {
    if (faxId) requestOpenFax('outgoing', faxId);
    window.history.pushState({ tab: 'outgoing' }, '', pathForTab('outgoing'));
    window.dispatchEvent(new PopStateEvent('popstate', { state: { tab: 'outgoing' } }));
    if (!inline) onClose?.();
  };
  const wrap = children => (inline
    ? <div style={S.inlineCard}>{children}</div>
    : <div style={S.overlay}><div style={S.modal} role="dialog" aria-modal="true" aria-label="Send a fax">{children}</div></div>);
  const tool = (icon, label, onClick, on) => (
    <button type="button" onClick={onClick} style={{ ...S.tool, ...(on ? S.toolOn : {}) }}>
      <Emo e={icon} style={{ fontSize: 18 }} /><span>{label}</span>
    </button>
  );
  // 3.64.0: several documents → the number of faxes they make
  const faxesMade = faxGroups(files).length > 1 ? faxCount(receivers.length ? receivers : [{}], files) : 0;
  const sendLabel = sending ? '⏳ Sending…'
    : faxesMade ? `${scheduleAt ? '🕒 Schedule' : '📤 Send'} ${faxesMade} faxes`
    : scheduleAt ? `🕒 Schedule${receivers.length > 1 ? ` for ${receivers.length} receivers` : ''}` : `📤 Send fax${receivers.length > 1 ? ` to ${receivers.length} receivers` : ''}`;

  return wrap(
    <>
      <div style={S.head} className="dlg-head">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <strong style={S.headTitle}>{inline ? 'New fax' : <><Emo e="📤" /> Send a fax</>}</strong>
          {files.length > 0 && <span style={S.headPages}>{totalPages != null ? `${coverEach > 1 ? 'about ' : ''}${totalPages} page${totalPages === 1 ? '' : 's'}${cover ? (faxGroups(files).length > 1 ? ' (with cover sheets)' : ' (with cover sheet)') : ''}` : 'pages counted when sent'}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {fromText && <span style={S.headFrom}>From {fromText}</span>}
          {!inline && <button onClick={onClose} style={S.closeBtn} disabled={sending} aria-label="Close">✕</button>}
        </div>
      </div>
      {result && view === 'track' && trackItems.length ? <SendTracker items={trackItems} progress={progress} setProgress={setProgress} inline={inline}
        onSkip={() => setView('sent')} onAnother={clearAll} onOutbox={() => openOutbox()} onOpenFax={id => openOutbox(id)} onClose={onClose} />
      : result ? <SentPanel lines={resultText(result)} scheduled={(result.results || []).some(x => x.scheduled)} inline={inline}
        onAnother={clearAll} onClose={onClose} onBack={() => setResult(null)} onOutbox={() => openOutbox()} onWatch={trackItems.length ? () => setView('track') : null} /> : <>
      <div style={S.toolbar}>
        {tool('👁️', 'Preview', showPreview, !!preview)}
        {tool('📇', 'Contacts', () => (picker ? setPicker(null) : openPicker()), !!picker)}
        {tool('🕒', 'Schedule', () => { setPicker(null); setPanel(p => (p === 'schedule' ? null : 'schedule')); }, panel === 'schedule' || !!scheduleAt)}
      </div>

      <div style={S.body}>
        {/* Schedule */}
        {panel === 'schedule' && (
          <div style={S.panel}>
            <label style={S.label}>Send at</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="datetime-local" value={scheduleAt} min={localInput(new Date(Date.now() + 5 * 60000))}
                max={localInput(new Date(Date.now() + 30 * 86400000))} onChange={e => setScheduleAt(e.target.value)} style={{ ...S.input, width: 240 }} />
              {scheduleAt && <button type="button" onClick={() => setScheduleAt('')} style={S.linkBtn}>Send now instead</button>}
            </div>
            <span style={S.hint}>Your time zone, up to 30 days ahead. It goes out within 5 minutes of that time, and waits in Outbox until then (you can cancel it there).</span>
            {hoursNote}
          </div>
        )}
        {scheduleAt && panel !== 'schedule' && (
          <><div style={S.schedNote}><Emo e="🕒" /> Scheduled for {new Date(scheduleAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })} · <button type="button" onClick={() => setScheduleAt('')} style={S.linkBtn}>send now instead</button></div>{hoursNote}</>
        )}
        {/* Send as client (Super Admins on "All clients", 3.27.0) */}
        {asAdmin && (
          <div style={S.grid2}>
            <div style={S.formGroup}>
              <label style={S.label}>Send as client</label>
              <select value={asClient} onChange={e => pickClient(e.target.value)} style={S.input} disabled={sending || !clients}>
                {(clients || [{ id: 'crazypixels', name: 'Crazy Pixels' }]).map(c => <option key={c.id} value={c.id}>{c.name}{c.type === 'individual' ? ' · Individual' : ''}{c.hipaa ? ' · HIPAA' : ''}</option>)}
              </select>
              <span style={S.hint}>Its fax line, contacts and rules are used, and the fax shows in its Outbox.</span>
            </div>
            {client?.from?.length > 1 && (
              <div style={S.formGroup}>
                <label style={S.label}>Send from</label>
                <select value={fromPick} onChange={e => setFromPick(Number(e.target.value))} style={S.input} disabled={sending}>
                  {client.from.map((o, i) => <option key={o.number + (o.teamId || '')} value={i}>{o.label ? `${o.label} · ` : ''}{fmtLine(o.number)}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Send from: only when there's a choice (teams or several lines) */}
        {(teams.length > 1 || lines.length > 1) && (
          <div style={S.formGroup}>
            <label style={S.label}>Send from</label>
            {teams.length > 1 ? (
              <select value={teamId} onChange={e => setTeamId(e.target.value)} style={S.input} disabled={sending}>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name} · {fmtLine(t.number)}</option>)}
              </select>
            ) : (
              <select value={line} onChange={e => setLine(e.target.value)} style={S.input} disabled={sending}>
                {lines.map((l, i) => <option key={l.number} value={l.number}>{fmtLine(l.number)}{l.label ? ` (${l.label})` : ''}{i === 0 ? ' · default' : ''}</option>)}
              </select>
            )}
          </div>
        )}

        {/* Cover sheet */}
        <div style={S.coverRow}>
          <label style={S.switchLabel}>
            <input type="checkbox" checked={cover} onChange={e => setCoverOn(e.target.checked)} />
            <span>Cover sheet</span>
          </label>
          {cover && (
            <label style={S.switchLabel} title="The standard notice for faxes with health information">
              <input type="checkbox" checked={notice} onChange={e => setNotice(e.target.checked)} />
              <span>Confidentiality notice{hipaa ? ' (HIPAA)' : ''}</span>
            </label>
          )}
        </div>

        {/* Receivers */}
        <div style={S.formGroup}>
          <label style={S.label}>Receivers{hipaa ? ' (one at a time)' : ''}</label>
          <div style={S.chipBox} className="cp-field-box" onClick={() => document.getElementById('compose-receiver')?.focus()}>
            {receivers.map(r => (
              <span key={r.number} style={S.chip}>
                {problemOf(r.number) && <span title={problemOf(r.number).label}><Emo e={problemOf(r.number).cls === 'not_fax' ? '⛔' : problemOf(r.number).cls === 'congested' ? '⏳' : '⚠️'} /></span>}
                {r.name && <strong>{r.name}</strong>} {fmtNumber(r.number)}
                <button type="button" onClick={() => removeReceiver(r.number)} style={S.chipX} aria-label={`Remove ${r.name || fmtNumber(r.number)}`}>×</button>
              </span>
            ))}
            {receivers.length < maxReceivers && (
              <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                <input id="compose-receiver" value={typed} disabled={sending}
                  onChange={e => {
                    // 3.32.2: a pasted list ("617 555 7777, 617 555 8888") adds each number
                    const parts = e.target.value.split(/[,;\n]+/);
                    if (parts.length < 2) { setTyped(e.target.value); return; }
                    const rest = parts.pop(), seen = new Set(receivers.map(r => digitsOf(r.number).slice(-10)));
                    let room = maxReceivers - receivers.length;
                    const left = [];   // not added: not a number, or no room
                    for (const p of parts.map(x => x.trim()).filter(Boolean)) {
                      const d = digitsOf(p).slice(-10);
                      if (!validNumber(p) || (!seen.has(d) && room <= 0)) { left.push(p); continue; }
                      if (seen.has(d)) continue;
                      seen.add(d); room--; addReceiver(p);
                    }
                    setTyped([...left, rest.trim()].filter(Boolean).join(', '));
                    if (left.length) setError(`Not added: ${left.join(', ')} (not a US or Canada fax number, or over ${maxReceivers === 1 ? 'one receiver' : `${maxReceivers} receivers`}).`);
                  }}
                  onFocus={() => suggest.length && setSuggestOpen(true)}
                  onBlur={() => {
                    setTimeout(() => setSuggestOpen(false), 150);
                    // 3.32.1: leaving the box with a whole number adds it (so its name can be typed)
                    if (typedNumber && receivers.length < maxReceivers) addReceiver(typedNumber);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'ArrowDown' && suggest.length) { e.preventDefault(); setHi(h => Math.min(suggest.length - 1, h + 1)); }
                    else if (e.key === 'ArrowUp' && suggest.length) { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
                    else if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
                      if (suggestOpen && hi >= 0 && suggest[hi]) { e.preventDefault(); addReceiver(suggest[hi].number, suggest[hi].name, true); }
                      else if (typed.trim() && validNumber(typed)) { e.preventDefault(); addReceiver(typed.trim()); }
                    } else if (e.key === 'Backspace' && !typed && receivers.length) removeReceiver(receivers[receivers.length - 1].number);
                    else if (e.key === 'Escape') setSuggestOpen(false);
                  }}
                  placeholder={receivers.length ? 'Add another…' : "A fax number or a contact's name"}
                  style={S.chipInput} role="combobox" aria-controls="compose-contact-list" aria-expanded={suggestOpen && suggest.length > 0} aria-autocomplete="list" autoComplete="off" />
                {suggestOpen && suggest.length > 0 && (
                  <ul id="compose-contact-list" role="listbox" style={S.suggestList}>
                    {suggest.map((c, i) => (
                      <li key={c.number + (c.accountName || '')} role="option" aria-selected={i === hi}
                        onMouseDown={e => { e.preventDefault(); addReceiver(c.number, c.name, true); }} onMouseEnter={() => setHi(i)}
                        style={{ ...S.suggestItem, ...(i === hi ? { background: 'var(--c-e0f2f1)' } : {}) }}>
                        <span style={{ minWidth: 0 }}>
                          {c.star && <span style={{ color: 'var(--t-f9a825)', marginRight: 4 }} title="Priority number">★</span>}
                          <strong>{c.label || c.name || 'Not listed'}</strong>{c.listing && <span style={{ color: 'var(--t-90a4ae)', fontSize: 11 }}> · {c.shared ? 'saved name' : 'phone listing'}</span>}
                          <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--t-546e7a)' }}>{fmtNumber(c.number)}</div>
                        </span>
                        {c.delivered != null && <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', color: c.delivered >= 90 ? 'var(--t-2e7d32)' : c.delivered >= 60 ? 'var(--t-e65100)' : 'var(--t-c62828)' }}>{c.delivered}% delivered</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <span style={S.hint}>{hipaa ? 'Your company handles health information, so each fax goes to one receiver.' : `Up to ${MAX_RECEIVERS}: separate numbers with a comma or Enter. Each document goes to each receiver as its own fax.`} Type a number or a contact’s name; a number already in Contacts brings its name.</span>
          {/* 3.60.0: a receiver on the Problem numbers list (wording approved by the owner, 2026-10-14) */}
          {receivers.filter(r => problemOf(r.number)).map(r => { const p = problemOf(r.number); return (
            <div key={`pn-${r.number}`} role="status" style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 13, lineHeight: 1.5,
              background: p.cls === 'not_fax' ? 'var(--c-fce4ec)' : 'var(--c-fff3e0)', color: p.cls === 'not_fax' ? 'var(--t-b71c1c)' : 'var(--t-e65100)' }}>
              {p.cls === 'not_fax'
                ? <><Emo e="⛔" /> <strong>{fmtNumber(r.number)} may not be a fax line.</strong> No fax to it got through in the last 30 days. It may be a voice line: call the number first. A fax line answers with a fax tone (a high-pitched beep); if a person or a voicemail answers, it’s a voice line. Please confirm the correct fax number with the receiver before sending.</>
                // 3.78.0 (owner, approved Nov 3): High failure congestion
                : p.cls === 'congested'
                ? <><Emo e="⚠️" /> This is a high failure congestion fax number: it's busy most of the day. Your fax goes out now. If it fails, it's retried automatically at the times this number usually takes faxes{p.goodHoursText ? ` (${p.goodHoursText})` : ''}, so there's no need to resend it.</>
                : <><Emo e="⚠️" /> <strong>{fmtNumber(r.number)} is hard to reach.</strong> {p.rate != null ? `Only ${Math.round(p.rate)}% of faxes to it got through in the last 30 days` : 'Many faxes to it failed in the last 30 days'}{p.mainReasonText ? ` (mostly: ${p.mainReasonText.toLowerCase()})` : ''}. Your fax will still be sent. To avoid more failures, contact the receiver to confirm their fax number and that their fax line is working, so your fax reaches them on time.</>}
            </div>); })}
        </div>

        {/* Contacts picker: choose receivers from the client's Contacts */}
        {picker && (() => {
          const full = receivers.length >= maxReceivers;
          const room = maxReceivers - receivers.length;
          return (
          <div style={S.panel}>
            <div style={{ fontWeight: 700, color: 'var(--t-00695c)', fontSize: 14, marginBottom: 4 }}>Choose receivers from Contacts{asAdmin && client ? ` (${client.name})` : ''}</div>
            <div style={{ ...S.hint, marginTop: 0, marginBottom: 8 }}>
              {full ? (hipaa ? 'Your company sends to one receiver at a time. Remove the receiver above to pick a different one.' : `You already have ${MAX_RECEIVERS} receivers.`)
                : hipaa ? 'Pick one, then click Add to receivers.' : `Tick up to ${room}, then click Add to receivers.`}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input value={picker.q} onChange={e => setPicker(p => ({ ...p, q: e.target.value }))} placeholder="Search a name or number" style={{ ...S.input, flex: 1 }} autoFocus />
              <button type="button" disabled={!picker.picked.size || full} onClick={() => {
                const rows = (picker.rows || []).filter(c => picker.picked.has(c.id));
                for (const c of rows) if (!addReceiver(c.number, c.receiverName ?? c.name, true)) break;   // 3.68.4: never a place or generic label
                setPicker(null);
              }} style={{ ...S.smallBtn, opacity: picker.picked.size && !full ? 1 : 0.5 }}>Add to receivers{picker.picked.size > 1 ? ` (${picker.picked.size})` : ''}</button>
              <button type="button" onClick={() => setPicker(null)} style={S.linkBtn}>Close</button>
            </div>
            <div style={S.pickList}>
              {picker.rows == null ? <div style={{ ...S.hint, padding: 10 }}>Loading…</div>
                : (() => {
                  const q = picker.q.trim().toLowerCase(), qd = digitsOf(q);
                  const matching = picker.rows.filter(c => !q || c.name.toLowerCase().includes(q) || c.note?.toLowerCase().includes(q) || (qd.length >= 3 && digitsOf(c.number).includes(qd)));
                  const shown = matching.slice(0, 100);
                  return shown.length ? <>
                    {shown.map(c => (
                      <label key={c.id} style={{ ...S.pickRow, ...(full ? { opacity: 0.55, cursor: 'default' } : {}) }}>
                        <input type={hipaa ? 'radio' : 'checkbox'} name="compose-pick" disabled={full} checked={picker.picked.has(c.id)} onChange={e => setPicker(p => {
                          const picked = hipaa ? new Set() : new Set(p.picked);
                          if (e.target.checked && (hipaa || picked.size < room)) picked.add(c.id); else picked.delete(c.id);
                          return { ...p, picked };
                        })} />
                        <span style={{ flex: 1, minWidth: 0 }}><strong>{c.label || c.name || 'Not listed'}</strong> <span style={{ fontFamily: 'monospace', color: 'var(--t-546e7a)', fontSize: 12 }}>{fmtNumber(c.number)}</span></span>
                        <span style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{c.sent} sent</span>
                      </label>
                    ))}
                    {matching.length > shown.length && <div style={{ ...S.hint, padding: '6px 10px' }}>Showing 100 of {matching.length}. Search to find others.</div>}
                  </> : <div style={{ ...S.hint, padding: 10 }}>No contacts match.</div>;
                })()}
            </div>
          </div>
          );
        })()}

        {/* Attachments */}
        <div style={S.formGroup}>
          <label style={S.label}>Attachments <span style={S.labelNote}>PDF, Word, JPG or PNG (each file its own fax; photos together as one) · up to 50 MB in all; over 150 pages goes as parts</span></label>
          <div style={{ ...S.dropZone, ...(drag ? S.dropZoneActive : {}) }}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); addFiles([...e.dataTransfer.files]); }}>
            <span style={{ fontSize: 20 }}><Emo e="📎" /></span> {phone ? <span><strong style={{ color: 'var(--t-00695c)' }}>Choose a file</strong> or take a photo</span>
              : <span>Drop files here or <strong style={{ color: 'var(--t-00695c)' }}>browse</strong></span>}
          </div>
          <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={e => addFiles([...e.target.files])} style={{ display: 'none' }} />
          {files.map((x, i) => (
            <div key={x.file.name + x.file.size} style={S.fileRow}>
              <span><Emo e="📄" /> {x.file.name} <span style={S.fileMeta}>{sizeText(x.file.size)}{x.pages != null ? ` · ${x.pages} page${x.pages === 1 ? '' : 's'}` : ''}</span></span>
              <button type="button" onClick={() => setFiles(fs => fs.filter((_, k) => k !== i))} style={S.fileX} aria-label={`Remove ${x.file.name}`}>✕</button>
            </div>
          ))}
          {hasWord && <span style={S.hint}>Word files are turned into PDF when you preview or send. This takes a few seconds; use Preview to check the pages.</span>}
        </div>

        {/* Cover sheet fields */}
        {cover && (
          <>
            <div style={S.grid2}>
              <div style={S.formGroup}>
                <label style={S.label}>Receiver name</label>
                {receivers.length > 1
                  ? (() => {
                      // 3.32.2: a name box for each new number; contacts keep their saved names
                      const fresh = receivers.filter(r => !r.fromContact);
                      const setName = (n, v) => setReceivers(rs => rs.map(r => (r.number === n ? { ...r, name: v } : r)));
                      return <>
                        {fresh.map(r => (
                          <div key={r.number} style={S.newName}>
                            <span style={S.newNameNum}>{fmtNumber(r.number)}</span>
                            <input value={r.name} onChange={e => setName(r.number, e.target.value)} placeholder="Receiver name" maxLength={80}
                              aria-label={`Receiver name for ${fmtNumber(r.number)}`} style={{ ...S.input, flex: 1, minWidth: 0 }} />
                          </div>
                        ))}
                        <div style={S.hint}>{fresh.length
                          ? `Each cover sheet names its own receiver. New ${fresh.length === 1 ? 'number' : 'numbers'}: the name is saved to Contacts when you send; the others use their Contacts names.`
                          : 'Each cover sheet names its own receiver, from Contacts.'}</div>
                      </>;
                    })()
                  : <input value={receivers[0]?.name || ''} disabled={!receivers.length && !typedNumber}
                      onFocus={() => { if (!receivers.length && typedNumber) addReceiver(typedNumber); }}
                      onChange={e => setReceivers(rs => rs.map((r, i) => (i === 0 ? { ...r, name: e.target.value } : r)))}
                      placeholder={receivers.length || typedNumber ? 'Receiver name' : 'Type or pick a receiver first'} maxLength={80} style={S.input} />}
                {receivers.length === 1 && (
                  <div style={S.hint}>{receivers[0].fromContact
                    ? 'From Contacts. Type the company’s real name to change it: when you send, you can save it for this number.'
                    : 'New number: this name is saved to Contacts when you send.'}</div>
                )}
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" maxLength={200} style={S.input} />
              </div>
            </div>
            {/* 3.32.0: the classic cover sheet ticks */}
            <div style={S.marks} role="group" aria-label="Cover sheet ticks">
              {COVER_MARKS.map(m => (
                <label key={m.id} style={S.mark}>
                  <input type="checkbox" checked={marks.includes(m.id)}
                    onChange={e => setMarks(ms => (e.target.checked ? COVER_MARKS.map(x => x.id).filter(id => id === m.id || ms.includes(id)) : ms.filter(id => id !== m.id)))} />
                  {m.label}
                </label>
              ))}
            </div>
            <div style={S.formGroup}>
              <label id="compose-message-label" style={S.label}>Cover sheet message <span style={{ ...S.counter, ...(msgChars > MAX_CHARS ? { color: 'var(--t-c62828)', fontWeight: 700 } : {}) }}>{msgChars.toLocaleString()}/{MAX_CHARS.toLocaleString()}</span></label>
              {/* 3.67.0 (owner): formatted, twice the old box's height; a long message continues on extra pages */}
              {/* lists sit tight, as they print on the fax */}
              <style>{'.cover-msg .et-rich ul,.cover-msg .et-rich ol{margin:0;padding-left:22px}'}</style>
              <div className="cover-msg"><RichText key={`msg-${msgKey}`} labelId="compose-message-label" initial={message} onChange={setMessageRaw} placeholders={false} pictures={false} links={false}
                cleanPaste={cleanPaste} minHeight={200} maxHeight="60vh" /></div>
              {coverEach > 1 && <div style={S.hint}>This message is longer than the cover sheet has room for, so the rest prints on {coverEach === 2 ? 'a second page' : `${coverEach - 1} more pages`}{faxGroups(files).length > 1 ? ' for each document' : ''}. Preview shows exactly how it prints.</div>}
            </div>
            {/* 3.32.0: Phone and Company, printed next to From and Email */}
            <div style={S.grid2}>
              <div style={S.formGroup}>
                <label style={S.label}>Your phone</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={contact.phone} onChange={e => setContactField('phone', e.target.value)} placeholder="Optional" maxLength={40} style={{ ...S.input, flex: 1, minWidth: 0 }}
                    onBlur={() => { const p = showPhone(contact.phone); if (p && p !== contact.phone) setContactField('phone', p); }} />
                  {/* 3.62.25 (owner): extension, printed after the phone ("ext. 123") */}
                  <input value={contact.ext || ''} onChange={e => setContactField('ext', e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="Ext." aria-label="Extension (optional)"
                    inputMode="numeric" style={{ ...S.input, width: 80, flex: '0 0 80px' }} title="Your extension (optional), from My profile" />
                </div>
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Company</label>
                <input value={contact.company} onChange={e => setContactField('company', e.target.value)} placeholder="Optional" maxLength={120} style={S.input} />
              </div>
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Other contact info <span style={S.labelNote}>address, direct line…</span> <span style={S.counter}>{contact.other.length}/500</span></label>
              <textarea value={contact.other} onChange={e => setContactField('other', e.target.value)} rows={2} maxLength={500} style={{ ...S.input, resize: 'vertical' }} placeholder="Optional" />
            </div>
          </>
        )}

        {splitNote && <div style={{ ...S.errorBox, background: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)', borderColor: 'var(--b-90caf9)' }}><Emo e="📄" /> {splitNote}</div>}
        {error && <div role="alert" style={S.errorBox}>{error}</div>}
      </div>

      <div style={S.footer}>
        <button onClick={handleSend} disabled={sending || !!result} style={S.sendBtn}>{emoLabel(sendLabel)}</button>
        {inline
          ? <button onClick={clearAll} disabled={sending} style={S.cancelBtn}>Clear</button>
          : <button onClick={onClose} disabled={sending} style={S.cancelBtn}>Cancel</button>}
      </div>
      </>}

      {/* 3.64.0: Send Confirmation */}
      {checking && <SendCheck receivers={checking.list} files={files} cover={cover} scheduleAt={scheduleAt} onClose={checking.resolve} />}

      {/* Preview */}
      {preview && (
        <div style={S.overlay} onClick={() => setPreview(null)}>
          <div style={S.previewBox} onClick={e => e.stopPropagation()}>
            <div style={S.head} className="dlg-head">
              <strong style={S.headTitle}>Preview{preview.pages ? ` · ${preview.pages} page${preview.pages === 1 ? '' : 's'}` : ''}</strong>
              {/* 3.64.1 (owner): each receiver's faxes (their name and number on the cover sheets) */}
              {receivers.length > 1 && (
                <label style={S.previewFor}>
                  <span>Showing the faxes for</span>
                  <select value={preview.for || 0} disabled={preview.loading} onChange={e => showPreview(Number(e.target.value))} style={S.previewForSelect} aria-label="Whose faxes the preview shows">
                    {receivers.map((r, k) => <option key={r.number} value={k}>{r.name ? `${r.name} · ` : ''}{fmtLine(r.number)}</option>)}
                  </select>
                </label>
              )}
              <button onClick={() => setPreview(null)} style={S.closeBtn} aria-label="Close preview">✕</button>
            </div>
            {preview.loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--t-78909c)' }}>Building the preview…</div>
              // 3.56.3 (owner, iPhone + Samsung): phones can't show a PDF in a frame (iPhone: the
              // first page only; Android: an Open box), so the pages are drawn as pictures, like a fax
              : needsPdfPages()
                ? <div style={S.previewPages}><PdfPages blob={preview.blob} fileName="the preview" /></div>
                : <iframe title="Fax preview" src={preview.url} style={{ width: '100%', height: '75vh', border: 'none' }} />}
          </div>
        </div>
      )}
    </>
  );
}

// Cover sheet ticks (3.32.0; the backend's lib/composeFax MARKS)
const COVER_MARKS = [
  { id: 'urgent', label: 'Urgent' },
  { id: 'reply', label: 'Please reply' },
  { id: 'review', label: 'For review' },
  { id: 'comment', label: 'Please comment' },
];

// 3.67.1 (owner, Oct 23): after Send, in place of the form: what went (or
// was scheduled, or not sent), and what next. Stays until a button is used.
function SentPanel({ lines, scheduled, inline, onAnother, onClose, onBack, onOutbox, onWatch }) {
  const failed = lines.filter(t => t.startsWith('✕')).length;
  const all = failed === lines.length;
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const title = all ? 'Not sent' : failed ? 'Some faxes weren’t sent'
    : scheduled ? (lines.length > 1 ? 'Your faxes are scheduled' : 'Your fax is scheduled')
    : lines.length > 1 ? 'Your faxes are on their way' : 'Your fax is on its way';
  return (
    <div style={S.sent} role="status">
      <div style={{ ...S.sentIcon, ...(failed ? S.sentIconBad : {}) }} aria-hidden="true">{failed ? '!' : '✓'}</div>
      <h2 ref={ref} tabIndex={-1} style={S.sentTitle}>{title}</h2>
      <div style={S.sentList}>
        {lines.map((t, i) => <div key={i} style={{ ...S.sentLine, ...(t.startsWith('✕') ? { color: 'var(--t-c62828)' } : {}) }}>{/^[✕✓]/.test(t) ? t : emoLabel(t)}</div>)}
      </div>
      {!all && <div style={S.sentNote}>{scheduled ? 'You can see or cancel it in Outbox until its time. ' : ''}You'll get an email when each fax is delivered or if it fails.</div>}
      <div style={S.sentBtns}>
        {failed > 0 && <button onClick={onBack} style={S.sentMain}>Go back to this fax</button>}
        <button onClick={onAnother} style={failed ? S.cancelBtn : S.sentMain}>Send another fax</button>
        {onWatch && <button onClick={onWatch} style={S.cancelBtn}><Emo e="👁️" /> Watch it</button>}
        <button onClick={onOutbox} style={S.cancelBtn}>Open Outbox</button>
        {!inline && <button onClick={onClose} style={S.cancelBtn}>Close</button>}
      </div>
    </div>
  );
}

const S = {
  newName: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  newNameNum: { fontFamily: 'monospace', fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap', minWidth: 118 },
  marks: { display: 'flex', flexWrap: 'wrap', gap: '8px 22px', margin: '2px 0 14px' },
  mark: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--t-37474f)', cursor: 'pointer' },
  counter: { float: 'right', fontWeight: 400, fontSize: 12, color: 'var(--t-90a4ae)' },
  previewPages: { maxHeight: '75vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 10, background: 'var(--c-eceff1)', boxSizing: 'border-box' },
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-000000-rgb) / 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  inlineCard: { background: 'var(--c-ffffff)', borderRadius: 14, maxWidth: 820, margin: '0 auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--c-e0f2f1)', overflow: 'visible' },
  modal: { background: 'var(--c-ffffff)', borderRadius: 16, width: '100%', maxWidth: 760, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.2)' },
  previewBox: { background: 'var(--c-ffffff)', borderRadius: 14, width: '100%', maxWidth: 860, overflow: 'hidden', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 20px', background: 'var(--c-f9fafb)', borderBottom: '1px solid var(--c-e0f2f1)', flexWrap: 'wrap' },
  headTitle: { color: 'var(--t-00695c)', fontSize: 17 },
  previewFor: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-546e7a)', flex: '1 1 auto', minWidth: 0, margin: '0 12px', flexWrap: 'wrap' },   // 3.64.1
  previewForSelect: { padding: '5px 8px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit', maxWidth: '100%', minWidth: 0, background: 'var(--c-ffffff)', color: 'var(--t-263238)' },
  headPages: { color: 'var(--t-78909c)', fontSize: 13 },
  headFrom: { color: 'var(--t-78909c)', fontSize: 12 },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--t-90a4ae)', padding: 4 },
  toolbar: { display: 'flex', gap: 6, padding: '6px 14px', borderBottom: '1px solid var(--c-eceff1)', flexWrap: 'wrap' },
  tool: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 12px', background: 'none', border: '1.5px solid transparent', borderRadius: 8, cursor: 'pointer', color: 'var(--t-00695c)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  toolOn: { background: 'var(--c-e0f2f1)', border: '1.5px solid var(--c-b2dfdb)' },
  body: { padding: '18px 22px' },
  panel: { background: 'var(--c-f7fbfa)', border: '1.5px solid var(--c-e0f2f1)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 },
  schedNote: { background: 'var(--c-fff8e1)', color: 'var(--t-8a5a00)', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 14 },
  hoursWarn: { background: 'var(--c-fff3e0)', color: 'var(--t-8a5a00)', border: '1px solid var(--c-ffe0b2)', borderRadius: 8, padding: '8px 12px', fontSize: 13, margin: '8px 0 14px', lineHeight: 1.5 },   // 3.61.0
  coverRow: { display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 14 },
  switchLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: 'var(--t-37474f)', cursor: 'pointer' },
  formGroup: { marginBottom: 16 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 },
  label: { display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--t-37474f)', marginBottom: 6 },
  labelNote: { fontWeight: 400, color: 'var(--t-78909c)', fontSize: 12 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, color: 'var(--t-333333)', fontFamily: 'inherit', boxSizing: 'border-box' },
  hint: { fontSize: 12, color: 'var(--t-90a4ae)', marginTop: 4, display: 'block' },
  chipBox: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: 6, border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, minHeight: 44, boxSizing: 'border-box', cursor: 'text' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--c-00897b)', color: 'var(--t-ffffff)', borderRadius: 6, padding: '4px 4px 4px 10px', fontSize: 13 },
  chipX: { background: 'rgb(var(--c-ffffff-rgb) / 0.2)', border: 'none', color: 'var(--t-ffffff)', borderRadius: 4, width: 20, height: 20, cursor: 'pointer', fontSize: 14, lineHeight: 1 },
  chipInput: { width: '100%', border: 'none', outline: 'none', fontSize: 14, padding: '6px 4px', fontFamily: 'inherit' },
  suggestList: { position: 'absolute', top: '100%', left: 0, right: 0, minWidth: 300, zIndex: 20, margin: '4px 0 0', padding: 4, listStyle: 'none', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 10, boxShadow: '0 6px 18px rgb(var(--c-000000-rgb) / 0.12)', maxHeight: 280, overflowY: 'auto' },
  suggestItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--t-37474f)' },
  pickList: { maxHeight: 240, overflowY: 'auto', background: 'var(--c-ffffff)', border: '1px solid var(--c-eceff1)', borderRadius: 8 },
  pickRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--c-f3f5f5)', fontSize: 13, cursor: 'pointer' },
  dropZone: { border: '2px dashed var(--c-cfd8dc)', borderRadius: 10, padding: '18px', textAlign: 'center', cursor: 'pointer', background: 'var(--c-fafafa)', color: 'var(--t-546e7a)', fontSize: 14 },
  dropZoneActive: { border: '2px solid var(--c-00897b)', background: 'var(--c-f0faf8)' },
  fileRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '7px 2px', borderBottom: '1px solid var(--c-f0f0f0)', fontSize: 13, color: 'var(--t-37474f)' },
  fileMeta: { color: 'var(--t-90a4ae)', fontSize: 12, marginLeft: 6 },
  fileX: { background: 'var(--c-fce4ec)', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 11, color: 'var(--t-b71c1c)' },
  smallBtn: { padding: '8px 14px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--t-00695c)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', padding: 0 },
  errorBox: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, whiteSpace: 'pre-line' },
  // 3.67.1: the Sent panel
  sent: { padding: '34px 22px 30px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 },
  sentIcon: { width: 56, height: 56, borderRadius: '50%', background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 800 },
  sentIconBad: { background: 'var(--c-fff3e0)', color: 'var(--t-e65100)' },
  sentTitle: { margin: 0, fontSize: 21, color: 'var(--t-263238)', outline: 'none' },
  sentList: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, color: 'var(--t-37474f)', maxWidth: 560, overflowWrap: 'anywhere' },
  sentLine: { lineHeight: 1.5 },
  sentNote: { fontSize: 13, color: 'var(--t-607d8b)', maxWidth: 480 },
  sentMain: { padding: '12px 22px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: 'inherit' },
  sentBtns: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 },
  successBox: { background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, lineHeight: 1.6 },
  footer: { display: 'flex', gap: 12, padding: '14px 22px', borderTop: '1px solid var(--c-f0f0f0)', background: 'var(--c-f9fafb)', borderRadius: '0 0 14px 14px' },
  sendBtn: { flex: 1, padding: '12px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: 'inherit' },
  cancelBtn: { padding: '12px 20px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
};
